import { Row, Col } from 'react-bootstrap';
import { FormattedMessage } from 'react-intl';
import capitalize from 'lodash/capitalize';
import flattenDeep from 'lodash/flattenDeep';
import uniq from 'lodash/uniq';

// Components
import {
  Card,
  Divider,
  Typography,
  Switch,
  Tooltip,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';
import { AlertInfoIcon } from '@fortress-technology-solutions/fortress-component-library/Icons';
import {
  SingleSelect,
  Grid,
} from '@fortress-technology-solutions/fortress-component-library/Molecules';
import { TextInput } from '../BasicDetails/TextInput';
import InfoIconToolTip from '../../../components/InfoIconToolTip';
import RDSetup from './Programs/RD';
import HUDSetup from './Programs/HUD';
import LIHTCSetup from './Programs/LIHTC';
import HOMESetup from './Programs/HOME';
import NOAHSetup from './Programs/NOAH';
import DeferredLoanSetup from './Programs/DeferredLoan';
import VHCBSetup from './Programs/VHCB';
import AHPSetup from './Programs/AHP';
import NSPSetup from './Programs/NSP';
import KCMOSetup from './Programs/KCMO';
import StLouisFundsSetup from './Programs/StLouisFunds';
import TCAPSetup from './Programs/TCAP';
import HTFSetup from './Programs/HTF';
import PHSetup from './Programs/PH';
import BONDSetup from './Programs/BOND';

import SetupGridItem from '../SetupGridItem';
import { SetupSwitch, SetupTitle } from './Programs/shared';
import ElementWithPermissions from '../../../components/ElementWithPermissions';

// Hooks
import { useFlags } from 'launchdarkly-react-client-sdk';
import {
  useFetchPassbookRatesByState,
  useFetchStateAffordableProgramConfigsByPropertyId,
} from './hooks';

// Utils
import {
  displayAsDashesIfEmpty,
  getProgramPassbookRatesDataByProgramName,
  stripOnlyWhiteSpaceToNull,
} from './utils';

// Constants
import { MINNESOTA_STATE_CODE } from '../constants';
import messages from './messages';
import integrationSetupMessages from '../IntegrationsSetup/messages';

// types
import type {
  AffordableDetails,
  AffordableSetupValues,
  RDValues,
  PHValues,
  LIHTCValues,
  HTFValues,
  LIHTCLikeValues,
  HOMEValues,
  PropertyStateConfigValues,
} from '../types';
import type { HUDValues } from './Programs/HUD';
import WaitlistV2 from './WaitlistV2';
import {
  getWaitlistProgramNames,
  resolveWaitlistPrograms,
} from './WaitlistV2/utils';
import MNHousingDNumber from './MNHousingDNumber';

type Props = {
  editMode: boolean,
  onChange: Function,
  promptToaster: Function,
  disabled: boolean,
} & AffordableSetupValues &
  AffordableDetails &
  PropertyStateConfigValues;

function AffordableSetup({
  organizationId,
  propertyId,
  state,
  promptToaster,
  intl,
  editMode: editModeProp,
  onChange,
  certificationPastDueDefinition,
  stateId,
  projectIdTic,
  HUDContractNumber,
  HUDProjectNumber,
  HUDiMaxNumber,
  HUDPropertyId,
  primaryContracts,
  secondaryContracts,
  HUDRepaymentCollectionFeePercentage,
  LIHTCContractEffectiveDate,
  LIHTCContractExpirationDate,
  RDContractEffectiveDate,
  RDContractExpirationDate,
  requiresInitialCertificationForNonHUD,
  XMLPropertyName,
  XMLProjectName,
  isOverrideQualificationEnabled,
  RDProjectNumber,
  RDProjectType,
  RDProjectRentalTypes,
  RDBorrowerId,
  loanTransferAmount,
  noteRentPayment,
  icPayment,
  RDProjectName,
  RDProgramType,
  RDTotalRAUnits,
  RD538Loan,
  RDNYStateContractNumber,
  isRDRAEnabled,
  hudPrimarySubsidyType,
  hudSecondarySubsidyType,
  HUDCompleteGRCertsWithoutSigned59As,
  HOMEAffordabilityPeriodStartDate,
  HOMEUnitType,
  HOMEAnnualIncomeType,
  HOMEPropertyType,
  HTFAffordabilityPeriodStartDate,
  HTFUnitType,
  autoSendToTRACS,
  autoRolloverIncomeAndAssets,
  interimCert,
  annualRecertMonth,
  affordableWaitlist,
  disabled,
  reserveAccountRequiredBalance,
  rdManagementFee,
  agencyName,
  phaCode,
  PHProgramType,
  PHProjectNumber,
  PHProjectSuffix,
  phProjects,
  PHMinimumRent,
  autoSendToPIC,
  stateConfigs,
  timezone,
  keyNumber,
  ...affordableDetails
}: Props) {
  const editMode = editModeProp && !disabled;
  const {
    initialCertsForLihtc,
    passbookRate,
    grossRentEnhancements,
    affordableWaitlist: affordableWaitlistFlag,
    affordableWaitlistV2: affordableWaitlistV2Flag,
  } = useFlags();
  const affordablePrograms = affordableDetails?.programs ?? [];
  const programNames = uniq(
    flattenDeep(
      affordablePrograms.map((pap) =>
        pap?.masterAffordableProgram?.name?.split('/'),
      ),
    ).filter(Boolean),
  );

  const programText = (affordableDetails?.programs ?? [])
    .map((p) => p?.masterAffordableProgram?.name)
    .filter(Boolean)
    .join(' ');

  const hasHUDProgram = programText.includes('HUD');
  const hasRDProgram = programText.includes('RD');
  const hasLIHTCProgram = programText.includes('LIHTC');
  const hasHOMEProgram = programText.includes('HOME');
  const hasNOAHProgram = programText.includes('NOAH');
  const hasDeferredLoanProgram = programText.includes('Deferred Loan');
  const hasVHCBProgram = programText.includes('VHCB');
  const hasAHPProgram = programText.includes('AHP');
  const hasNSPProgram = programText.includes('NSP');
  const hasKCMOProgram = programText.includes('KCMO');
  const hasStLouisFundsProgram = programText.includes('St. Louis Funds');
  const hasHTFProgram = programText.includes('HTF');
  const hasTCAPProgram = programText.includes('TCAP');
  const hasPHProgram = programNames.includes('PH');
  const hasBONDProgram = programNames.includes('BOND');

  const hasHUDProgramOnly =
    hasHUDProgram && programNames.length === 1 && programNames.length !== 0;

  const affordableProgramNames = getWaitlistProgramNames(affordablePrograms);

  const { data: statePassbookRates, isLoading: isLoadingPassbookRates } =
    useFetchPassbookRatesByState({
      organizationId,
      propertyId,
      state,
    });

  const { data: stateAffordableProgramConfigs } =
    useFetchStateAffordableProgramConfigsByPropertyId({
      propertyId,
    });

  const RDSetupValues: RDValues = {
    RDProjectNumber,
    RDProjectType,
    RDProjectRentalTypes,
    RDBorrowerId,
    loanTransferAmount,
    noteRentPayment,
    icPayment,
    RDProjectName,
    RDProgramType,
    RDTotalRAUnits,
    RD538Loan,
    isRDRAEnabled,
    RDContractEffectiveDate,
    RDContractExpirationDate,
    RDNYStateContractNumber,
    autoRolloverIncomeAndAssets: autoRolloverIncomeAndAssets.RD,
    RDAnnualRecertMonth: annualRecertMonth.RD,
    reserveAccountRequiredBalance,
    flags: {
      passbookRate,
    },
    passbookRates: getProgramPassbookRatesDataByProgramName(
      'RD',
      statePassbookRates,
    ),
    intl,
    managementFee: {
      nonContinuousEnabled: rdManagementFee.nonContinuousParcelsEnabled,
      remoteLocationEnabled: rdManagementFee.propertiesInRemoteEnabled,
      troubledOrCriticalEnabled: rdManagementFee.troubledOrCriticalEnabled,
    },
    hotmaConfig: stateAffordableProgramConfigs?.RD,
    certificationPastDueDefinition: certificationPastDueDefinition?.RD,
  };
  const HUDSetupValues: HUDValues = {
    HUDProjectNumber,
    HUDiMaxNumber,
    HUDPropertyId,
    primaryContracts,
    secondaryContracts,
    hudPrimarySubsidyType,
    hudSecondarySubsidyType,
    HUDCompleteGRCertsWithoutSigned59As,
    autoRolloverIncomeAndAssets: autoRolloverIncomeAndAssets.HUD,
    HUDAnnualRecertMonth: annualRecertMonth.HUD,
    passbookRates: getProgramPassbookRatesDataByProgramName(
      'HUD',
      statePassbookRates,
    ),
    autoSendToTRACS,
    HUDRepaymentCollectionFeePercentage,
    intl,
    flags: {
      grossRentEnhancements,
    },
    hotmaConfig: stateAffordableProgramConfigs?.HUD,
    certificationPastDueDefinition: certificationPastDueDefinition?.HUD,
  };
  const LIHTCSetupValues: LIHTCValues = {
    stateId,
    projectIdTic,
    XMLPropertyName,
    XMLProjectName,
    LIHTCContractEffectiveDate,
    LIHTCContractExpirationDate,
    requiresInitialCertificationForNonHUD:
      requiresInitialCertificationForNonHUD.LIHTC,
    isOverrideQualificationEnabled: isOverrideQualificationEnabled.LIHTC,
    autoRolloverIncomeAndAssets: autoRolloverIncomeAndAssets.LIHTC,
    annualRecertMonth: annualRecertMonth.LIHTC,
    interimCert: interimCert.LIHTC,
    flags: {
      initialCertsRequired: initialCertsForLihtc,
      hasHUDProgramOnly,
    },
    passbookRates: getProgramPassbookRatesDataByProgramName(
      'LIHTC',
      statePassbookRates,
    ),
    intl,
    hotmaConfig: stateAffordableProgramConfigs?.LIHTC,
    certificationPastDueDefinition: certificationPastDueDefinition?.LIHTC,
    keyNumber,
  };
  const HOMESetupValues: HOMEValues = {
    HOMEAffordabilityPeriodStartDate,
    HOMEUnitType,
    HOMEAnnualIncomeType,
    HOMEPropertyType,
    autoRolloverIncomeAndAssets: autoRolloverIncomeAndAssets.HOME,
    HOMEAnnualRecertMonth: annualRecertMonth.HOME,
    flags: {
      passbookRate,
    },
    passbookRates: getProgramPassbookRatesDataByProgramName(
      'HOME',
      statePassbookRates,
    ),
    intl,
    hotmaConfig:
      stateAffordableProgramConfigs?.HOME ??
      stateAffordableProgramConfigs?.LIHTC,
    certificationPastDueDefinition: certificationPastDueDefinition?.HOME,
  };
  const HTFSetupValues: HTFValues = {
    HTFAffordabilityPeriodStartDate,
    HTFUnitType,
    autoRolloverIncomeAndAssets: autoRolloverIncomeAndAssets.HTF,
    HTFAnnualRecertMonth: annualRecertMonth.HTF,
    flags: {
      passbookRate,
    },
    passbookRates: getProgramPassbookRatesDataByProgramName(
      'HTF',
      statePassbookRates,
    ),
    intl,
    hotmaConfig:
      stateAffordableProgramConfigs?.HTF ??
      stateAffordableProgramConfigs?.LIHTC,
    certificationPastDueDefinition: certificationPastDueDefinition?.HTF,
  };
  const PHSetupValues: PHValues = {
    agencyName,
    phaCode,
    PHProgramType,
    PHProjectNumber,
    PHProjectSuffix,
    phProjects: phProjects ?? [],
    PHMinimumRent,
    autoSendToPIC,
    passbookRates: getProgramPassbookRatesDataByProgramName(
      'PH',
      statePassbookRates,
    ),
    PHAnnualRecertMonth: annualRecertMonth.PH,
    autoRolloverIncomeAndAssets: autoRolloverIncomeAndAssets.PH,
    hotmaConfig: stateAffordableProgramConfigs?.['PH'],
    intl,
    certificationPastDueDefinition: certificationPastDueDefinition?.PH,
  };

  const lihtcLikeFlags = {
    initialCertsRequired: initialCertsForLihtc,
    hasHUDProgramOnly,
  };
  const NOAHSetupValues: LIHTCLikeValues = {
    requiresInitialCertificationForNonHUD:
      requiresInitialCertificationForNonHUD.NOAH,
    isOverrideQualificationEnabled: isOverrideQualificationEnabled.NOAH,
    autoRolloverIncomeAndAssets: autoRolloverIncomeAndAssets.NOAH,
    annualRecertMonth: annualRecertMonth.NOAH,
    interimCert: interimCert.NOAH,
    flags: lihtcLikeFlags,
    passbookRates: getProgramPassbookRatesDataByProgramName(
      'NOAH',
      statePassbookRates,
    ),
    intl,
    hotmaConfig:
      stateAffordableProgramConfigs?.NOAH ??
      stateAffordableProgramConfigs?.LIHTC,
    certificationPastDueDefinition: certificationPastDueDefinition?.NOAH,
  };
  const DeferredLoanSetupValues: LIHTCLikeValues = {
    requiresInitialCertificationForNonHUD:
      requiresInitialCertificationForNonHUD.DeferredLoan,
    isOverrideQualificationEnabled: isOverrideQualificationEnabled.DeferredLoan,
    autoRolloverIncomeAndAssets: autoRolloverIncomeAndAssets.DeferredLoan,
    annualRecertMonth: annualRecertMonth.DeferredLoan,
    interimCert: interimCert.DeferredLoan,
    flags: lihtcLikeFlags,
    passbookRates: getProgramPassbookRatesDataByProgramName(
      'Deferred Loan',
      statePassbookRates,
    ),
    intl,
    hotmaConfig:
      stateAffordableProgramConfigs?.['Deferred Loan'] ??
      stateAffordableProgramConfigs?.LIHTC,
    certificationPastDueDefinition:
      certificationPastDueDefinition?.DeferredLoan,
  };
  const VHCBSetupValues: LIHTCLikeValues = {
    requiresInitialCertificationForNonHUD:
      requiresInitialCertificationForNonHUD.VHCB,
    isOverrideQualificationEnabled: isOverrideQualificationEnabled.VHCB,
    autoRolloverIncomeAndAssets: autoRolloverIncomeAndAssets.VHCB,
    annualRecertMonth: annualRecertMonth.VHCB,
    interimCert: interimCert.VHCB,
    flags: lihtcLikeFlags,
    passbookRates: getProgramPassbookRatesDataByProgramName(
      'VHCB',
      statePassbookRates,
    ),
    intl,
    hotmaConfig:
      stateAffordableProgramConfigs?.VHCB ??
      stateAffordableProgramConfigs?.LIHTC,
    certificationPastDueDefinition: certificationPastDueDefinition?.VHCB,
  };
  const AHPSetupValues: LIHTCLikeValues = {
    requiresInitialCertificationForNonHUD:
      requiresInitialCertificationForNonHUD.AHP,
    isOverrideQualificationEnabled: isOverrideQualificationEnabled.AHP,
    autoRolloverIncomeAndAssets: autoRolloverIncomeAndAssets.AHP,
    annualRecertMonth: annualRecertMonth.AHP,
    interimCert: interimCert.AHP,
    flags: lihtcLikeFlags,
    passbookRates: getProgramPassbookRatesDataByProgramName(
      'AHP',
      statePassbookRates,
    ),
    intl,
    hotmaConfig:
      stateAffordableProgramConfigs?.AHP ??
      stateAffordableProgramConfigs?.LIHTC,
    certificationPastDueDefinition: certificationPastDueDefinition?.AHP,
  };
  const NSPSetupValues: LIHTCLikeValues = {
    requiresInitialCertificationForNonHUD:
      requiresInitialCertificationForNonHUD.NSP,
    isOverrideQualificationEnabled: isOverrideQualificationEnabled.NSP,
    autoRolloverIncomeAndAssets: autoRolloverIncomeAndAssets.NSP,
    annualRecertMonth: annualRecertMonth.NSP,
    interimCert: interimCert.NSP,
    flags: lihtcLikeFlags,
    passbookRates: getProgramPassbookRatesDataByProgramName(
      'NSP',
      statePassbookRates,
    ),
    intl,
    hotmaConfig:
      stateAffordableProgramConfigs?.NSP ??
      stateAffordableProgramConfigs?.LIHTC,
    certificationPastDueDefinition: certificationPastDueDefinition?.NSP,
  };
  const KCMOSetupValues: LIHTCLikeValues = {
    requiresInitialCertificationForNonHUD:
      requiresInitialCertificationForNonHUD.KCMO,
    isOverrideQualificationEnabled: isOverrideQualificationEnabled.KCMO,
    autoRolloverIncomeAndAssets: autoRolloverIncomeAndAssets.KCMO,
    annualRecertMonth: annualRecertMonth.KCMO,
    interimCert: interimCert.KCMO,
    flags: lihtcLikeFlags,
    passbookRates: getProgramPassbookRatesDataByProgramName(
      'KCMO',
      statePassbookRates,
    ),
    intl,
    hotmaConfig:
      stateAffordableProgramConfigs?.KCMO ??
      stateAffordableProgramConfigs?.LIHTC,
    certificationPastDueDefinition: certificationPastDueDefinition?.KCMO,
  };
  const StLouisFundsSetupValues: LIHTCLikeValues = {
    requiresInitialCertificationForNonHUD:
      requiresInitialCertificationForNonHUD.StLouisFunds,
    isOverrideQualificationEnabled: isOverrideQualificationEnabled.StLouisFunds,
    autoRolloverIncomeAndAssets: autoRolloverIncomeAndAssets.StLouisFunds,
    annualRecertMonth: annualRecertMonth.StLouisFunds,
    interimCert: interimCert.StLouisFunds,
    flags: lihtcLikeFlags,
    passbookRates: getProgramPassbookRatesDataByProgramName(
      'St. Louis Funds',
      statePassbookRates,
    ),
    intl,
    hotmaConfig:
      stateAffordableProgramConfigs?.['St. Louis Funds'] ??
      stateAffordableProgramConfigs?.LIHTC,
    certificationPastDueDefinition:
      certificationPastDueDefinition?.StLouisFunds,
  };
  const TCAPSetupValues: LIHTCLikeValues = {
    requiresInitialCertificationForNonHUD:
      requiresInitialCertificationForNonHUD.TCAP,
    isOverrideQualificationEnabled: isOverrideQualificationEnabled.TCAP,
    autoRolloverIncomeAndAssets: autoRolloverIncomeAndAssets.TCAP,
    annualRecertMonth: annualRecertMonth.TCAP,
    interimCert: interimCert.TCAP,
    flags: lihtcLikeFlags,
    passbookRates: getProgramPassbookRatesDataByProgramName(
      'TCAP',
      statePassbookRates,
    ),
    intl,
    hotmaConfig:
      stateAffordableProgramConfigs?.TCAP ??
      stateAffordableProgramConfigs?.LIHTC,
    certificationPastDueDefinition: certificationPastDueDefinition?.TCAP,
  };
  const BONDSetupValues: LIHTCLikeValues = {
    requiresInitialCertificationForNonHUD:
      requiresInitialCertificationForNonHUD.BOND,
    isOverrideQualificationEnabled: isOverrideQualificationEnabled.BOND,
    autoRolloverIncomeAndAssets: autoRolloverIncomeAndAssets.BOND,
    annualRecertMonth: annualRecertMonth.BOND,
    interimCert: interimCert.BOND,
    flags: lihtcLikeFlags,
    passbookRates: getProgramPassbookRatesDataByProgramName(
      'BOND',
      statePassbookRates,
    ),
    intl,
    hotmaConfig:
      stateAffordableProgramConfigs?.BOND ??
      stateAffordableProgramConfigs?.LIHTC,
    certificationPastDueDefinition: certificationPastDueDefinition?.BOND,
  };

  const anyHUDSubsidyType = affordableDetails?.hudSubsidyType;
  const anyHUDSecondarySubsidyType = affordableDetails?.hudSecondarySubsidyType;
  const infoTipMessage = (
    <>
      <p style={{ fontSize: '12px', textAlign: 'center' }}>
        <FormattedMessage {...messages.initialCertForNonHUDTip} />
      </p>
    </>
  );
  // This function helps to convert the new Switch data to match parent form utility function
  const handleSwitchChange = (evt) => {
    const name = evt.target.name;
    const value = evt.target.checked;
    onChange({ target: { name, value } });
  };
  const showWaitlistV1 =
    affordableWaitlistFlag &&
    !affordableWaitlistV2Flag &&
    (hasHUDProgram || hasPHProgram);

  const affordableProgramOptions = affordableProgramNames.map((name) => ({
    label: name,
    value: name,
  }));

  const savedWaitlistPrograms = resolveWaitlistPrograms(
    affordableWaitlist?.programs,
    affordablePrograms,
  );

  const selectedWaitlistPrograms = savedWaitlistPrograms
    .map((p) => {
      const val = typeof p === 'string' ? p : p?.value || p?.label || p?.name;
      return val ? { label: val, value: val } : null;
    })
    .filter(Boolean)
    .filter((sel) =>
      affordableProgramOptions.some((opt) => opt.value === sel.value),
    );

  const waitlistV2Values = {
    organizationId,
    propertyId,
    affordableProgramOptions,
    selectedAffordablePrograms: selectedWaitlistPrograms,
    affordableWaitlistEnabled: Boolean(affordableWaitlist?.enabled),
    affordableWaitlistOpen: affordableWaitlist?.status === 'OPEN',
    separateByBedroom: affordableWaitlist?.separateByBedroom ?? false,
    usePreferences: affordableWaitlist?.usePreferences ?? false,
    preferenceWeightMethod: affordableWaitlist?.preferenceWeightMethod ?? null,
    openDate: affordableWaitlist?.openDate ?? null,
    closeDate: affordableWaitlist?.closeDate ?? null,
    timezone: timezone ?? 'UTC',
    preferences: affordableWaitlist?.preferences ?? [],
  };

  return (
    <Card sx={{ padding: 2 }}>
      <Typography variant={'titleLarge'}>
        <FormattedMessage {...messages.title} />
      </Typography>
      <Divider sx={{ marginY: 2 }} />
      {passbookRate ? (
        <>
          {/* First the programs the property has and, on a Minnesota
              property, its D Number. The waitlist, with its own program
              selector, is a separate section below. */}
          <Grid container spacing={2}>
            <SetupGridItem
              label={<FormattedMessage {...messages.affordablePrograms} />}
            >
              <Typography variant="bodyMedium">
                {affordableProgramNames.join(' / ')}
              </Typography>
            </SetupGridItem>
            <MNHousingDNumber
              editMode={editMode}
              onChange={onChange}
              state={state}
              stateConfigs={stateConfigs}
            />
          </Grid>
          {(affordableWaitlistV2Flag || showWaitlistV1) && (
            <>
              <Divider sx={{ marginY: 2 }} />
              <Grid container spacing={2}>
                {affordableWaitlistV2Flag && (
                  <>
                    <SetupTitle>
                      <FormattedMessage {...messages.waitlistV2Title} />
                    </SetupTitle>
                    <WaitlistV2
                      editMode={editMode}
                      onChange={onChange}
                      values={waitlistV2Values}
                    />
                  </>
                )}
                {showWaitlistV1 && (
                  <>
                    <SetupGridItem
                      xs={12}
                      sm={affordableWaitlist.enabled ? 4 : 6}
                      label={
                        <FormattedMessage {...messages.affordableWaitlist} />
                      }
                      TooltipProps={{
                        title: (
                          <FormattedMessage
                            {...messages.affordableWaitlistTooltip}
                          />
                        ),
                      }}
                    >
                      <SetupSwitch
                        switchId={'affordable-waitlist-switch'}
                        editMode={editMode}
                        name="setup.affordableWaitlist"
                        disabled={!editMode || !(hasHUDProgram || hasPHProgram)}
                        value={affordableWaitlist.enabled}
                        onChange={onChange}
                      />
                    </SetupGridItem>
                    {affordableWaitlist.enabled && (
                      <SetupGridItem
                        xs={12}
                        sm={4}
                        label={
                          <FormattedMessage
                            {...messages.affordableWaitlistStatus}
                          />
                        }
                      >
                        {editMode ? (
                          <ElementWithPermissions
                            scope={['affordable-waitlist-open-close']}
                          >
                            <SingleSelect
                              options={[
                                {
                                  value: 'OPEN',
                                  text: 'Open',
                                },
                                {
                                  value: 'CLOSED',
                                  text: 'Closed',
                                },
                              ]}
                              value={affordableWaitlist.status}
                              onChange={(value) => {
                                onChange({
                                  target: {
                                    name: 'setup.affordableWaitlistStatus',
                                    value,
                                  },
                                });
                              }}
                            />
                          </ElementWithPermissions>
                        ) : (
                          <Typography
                            color={
                              affordableWaitlist.status === 'OPEN'
                                ? 'green'
                                : 'firebrick'
                            }
                          >
                            {capitalize(affordableWaitlist.status)}
                          </Typography>
                        )}
                      </SetupGridItem>
                    )}
                  </>
                )}
              </Grid>
            </>
          )}
          {(hasHUDProgram || hasLIHTCProgram) && (
            <Divider sx={{ marginY: 2 }} />
          )}
          {hasHUDProgram && (
            <HUDSetup
              promptToaster={promptToaster}
              isLoadingPassbookRateData={isLoadingPassbookRates}
              editMode={editMode}
              onChange={onChange}
              values={HUDSetupValues}
            />
          )}
          {hasPHProgram && (
            <>
              <Divider sx={{ marginY: 2 }} />
              <PHSetup
                promptToaster={promptToaster}
                isLoadingPassbookRateData={isLoadingPassbookRates}
                editMode={editMode}
                onChange={onChange}
                values={PHSetupValues}
                intl={intl}
              />
            </>
          )}
          {hasLIHTCProgram && (
            <>
              {(hasHUDProgram || hasPHProgram) && (
                <Divider sx={{ marginY: 2 }} />
              )}
              <LIHTCSetup
                promptToaster={promptToaster}
                isLoadingPassbookRateData={isLoadingPassbookRates}
                editMode={editMode}
                onChange={onChange}
                values={LIHTCSetupValues}
                state={state}
                stateConfigs={stateConfigs}
              />
            </>
          )}
          {hasNOAHProgram && (
            <>
              <Divider sx={{ marginY: 2 }} />
              <NOAHSetup
                promptToaster={promptToaster}
                isLoadingPassbookRateData={isLoadingPassbookRates}
                editMode={editMode}
                onChange={onChange}
                values={NOAHSetupValues}
                state={state}
                stateConfigs={stateConfigs}
              />
            </>
          )}
          {hasDeferredLoanProgram && (
            <>
              <Divider sx={{ marginY: 2 }} />
              <DeferredLoanSetup
                promptToaster={promptToaster}
                isLoadingPassbookRateData={isLoadingPassbookRates}
                editMode={editMode}
                onChange={onChange}
                values={DeferredLoanSetupValues}
                state={state}
                stateConfigs={stateConfigs}
              />
            </>
          )}
          {hasVHCBProgram && (
            <>
              <Divider sx={{ marginY: 2 }} />
              <VHCBSetup
                promptToaster={promptToaster}
                isLoadingPassbookRateData={isLoadingPassbookRates}
                editMode={editMode}
                onChange={onChange}
                values={VHCBSetupValues}
                state={state}
                stateConfigs={stateConfigs}
              />
            </>
          )}
          {hasNSPProgram && (
            <>
              <Divider sx={{ marginY: 2 }} />
              <NSPSetup
                promptToaster={promptToaster}
                isLoadingPassbookRateData={isLoadingPassbookRates}
                editMode={editMode}
                onChange={onChange}
                values={NSPSetupValues}
                state={state}
                stateConfigs={stateConfigs}
              />
            </>
          )}
          {hasKCMOProgram && (
            <>
              <Divider sx={{ marginY: 2 }} />
              <KCMOSetup
                promptToaster={promptToaster}
                isLoadingPassbookRateData={isLoadingPassbookRates}
                editMode={editMode}
                onChange={onChange}
                values={KCMOSetupValues}
                state={state}
                stateConfigs={stateConfigs}
              />
            </>
          )}
          {hasStLouisFundsProgram && (
            <>
              <Divider sx={{ marginY: 2 }} />
              <StLouisFundsSetup
                promptToaster={promptToaster}
                isLoadingPassbookRateData={isLoadingPassbookRates}
                editMode={editMode}
                onChange={onChange}
                values={StLouisFundsSetupValues}
                state={state}
                stateConfigs={stateConfigs}
              />
            </>
          )}
          {hasAHPProgram && (
            <>
              <Divider sx={{ marginY: 2 }} />
              <AHPSetup
                promptToaster={promptToaster}
                isLoadingPassbookRateData={isLoadingPassbookRates}
                editMode={editMode}
                onChange={onChange}
                values={AHPSetupValues}
                state={state}
                stateConfigs={stateConfigs}
              />
            </>
          )}
          {hasTCAPProgram && (
            <>
              <Divider sx={{ marginY: 2 }} />
              <TCAPSetup
                promptToaster={promptToaster}
                isLoadingPassbookRateData={isLoadingPassbookRates}
                editMode={editMode}
                onChange={onChange}
                values={TCAPSetupValues}
                state={state}
                stateConfigs={stateConfigs}
              />
            </>
          )}
          {hasHOMEProgram && (
            <>
              <Divider sx={{ marginY: 2 }} />
              <HOMESetup
                onChange={onChange}
                promptToaster={promptToaster}
                isLoadingPassbookRateData={isLoadingPassbookRates}
                editMode={editMode}
                values={HOMESetupValues}
                state={state}
                stateConfigs={stateConfigs}
              />
            </>
          )}
          {hasBONDProgram && (
            <>
              <Divider sx={{ marginY: 2 }} />
              <BONDSetup
                onChange={onChange}
                promptToaster={promptToaster}
                isLoadingPassbookRateData={isLoadingPassbookRates}
                editMode={editMode}
                values={BONDSetupValues}
                state={state}
                stateConfigs={stateConfigs}
              />
            </>
          )}
          {hasHTFProgram && (
            <>
              <Divider sx={{ marginY: 2 }} />
              <HTFSetup
                onChange={onChange}
                promptToaster={promptToaster}
                isLoadingPassbookRateData={isLoadingPassbookRates}
                editMode={editMode}
                values={HTFSetupValues}
                state={state}
                stateConfigs={stateConfigs}
              />
            </>
          )}
          {hasRDProgram && (
            <>
              <Divider sx={{ marginY: 2 }} />
              <RDSetup
                promptToaster={promptToaster}
                isLoadingPassbookRateData={isLoadingPassbookRates}
                editMode={editMode}
                onChange={onChange}
                values={RDSetupValues}
                propertyState={state}
              />
            </>
          )}
        </>
      ) : (
        //  // We can probably remove this whole block at this point, LIHTC setup is a separate component
        <>
          {state === MINNESOTA_STATE_CODE && (
            <>
              <Grid container spacing={2}>
                <MNHousingDNumber
                  editMode={editMode}
                  onChange={onChange}
                  state={state}
                  stateConfigs={stateConfigs}
                />
              </Grid>
              <Divider sx={{ marginY: 2 }} />
            </>
          )}
          <div className="container-fluid panel__details">
            <Row>
              <Col xs={12} sm={6}>
                <Row>
                  <Col xs={12} md={5}>
                    <strong>
                      <FormattedMessage {...messages.stateProjectIdXml} />
                    </strong>
                  </Col>
                  <Col xs={12} md={7}>
                    {editMode ? (
                      <TextInput
                        name="setup.stateId"
                        value={stateId}
                        editMode={editMode}
                        required
                        placeholder="i.e. AR012-345"
                        onChange={onChange}
                      />
                    ) : (
                      stateId
                    )}
                  </Col>
                </Row>
                <Row>
                  <Col xs={12} md={5}>
                    <strong>
                      <FormattedMessage {...messages.stateProjectIdTicHec} />
                    </strong>
                  </Col>
                  <Col xs={12} md={7}>
                    {editMode ? (
                      <TextInput
                        name="setup.projectIdTic"
                        value={projectIdTic}
                        editMode={editMode}
                        required
                        placeholder="i.e. AR012-345"
                        onChange={onChange}
                      />
                    ) : (
                      projectIdTic
                    )}
                  </Col>
                </Row>
                {hasHUDProgram && (
                  <>
                    <Row>
                      <Col xs={12} md={5}>
                        <strong>
                          <FormattedMessage {...messages.hudSubsidyType} />
                        </strong>
                      </Col>
                      <Col xs={12} md={7}>
                        {anyHUDSubsidyType
                          ? affordableDetails.hudSubsidyType
                          : '---'}
                      </Col>
                    </Row>
                    <Row>
                      <Col xs={12} md={5}>
                        <strong>
                          <FormattedMessage {...messages.hudProjectNumber} />
                        </strong>
                      </Col>
                      <Col xs={12} md={7}>
                        {editMode ? (
                          <TextInput
                            name="setup.HUDProjectNumber"
                            value={HUDProjectNumber}
                            editMode={editMode}
                            required
                            placeholder="i.e. 06475404"
                            onChange={onChange}
                          />
                        ) : (
                          HUDProjectNumber
                        )}
                      </Col>
                    </Row>
                  </>
                )}
              </Col>
              <Col xs={12} sm={6}>
                <Row>
                  <Col xs={12} md={5}>
                    <strong>
                      <FormattedMessage {...messages.affordablePrograms} />
                    </strong>
                  </Col>
                  <Col xs={12} md={7}>
                    {programNames.join(' / ')}
                  </Col>
                </Row>
                {!hasHUDProgramOnly && initialCertsForLihtc && (
                  <Row>
                    <Col xs={12} md={5}>
                      <strong>
                        <FormattedMessage {...messages.initialCertForNonHUD} />
                        <span style={{ paddingLeft: 4 }}>
                          <InfoIconToolTip
                            placement={'top'}
                            message={infoTipMessage}
                          />
                        </span>
                      </strong>
                    </Col>
                    <Col
                      xs={12}
                      md={7}
                      className={
                        requiresInitialCertificationForNonHUD
                          ? 'integration-on'
                          : 'integration-off'
                      }
                    >
                      {editMode ? (
                        <Switch
                          name="setup.requiresInitialCertificationForNonHUD"
                          id="initial-cert-for-non-HUD-switch"
                          checked={requiresInitialCertificationForNonHUD}
                          disabled={!editMode}
                          onChange={handleSwitchChange}
                        />
                      ) : requiresInitialCertificationForNonHUD ? (
                        <FormattedMessage {...integrationSetupMessages.on} />
                      ) : (
                        <FormattedMessage {...integrationSetupMessages.off} />
                      )}
                    </Col>
                  </Row>
                )}
                {hasHUDProgram && (
                  <>
                    <Row>
                      <Col xs={12} md={5}>
                        <strong>
                          <FormattedMessage
                            {...messages.hudSecondarySubsidyType}
                          />
                        </strong>
                      </Col>
                      <Col xs={12} md={7}>
                        {anyHUDSecondarySubsidyType
                          ? affordableDetails.hudSecondarySubsidyType
                          : '---'}
                      </Col>
                    </Row>
                    <Row>
                      <Col xs={12} md={5}>
                        <strong>
                          <FormattedMessage {...messages.hudContractNumber} />
                        </strong>
                      </Col>
                      <Col xs={12} md={7}>
                        {editMode ? (
                          <TextInput
                            name="setup.HUDContractNumber"
                            value={HUDContractNumber}
                            editMode={editMode}
                            required
                            placeholder="i.e. TN430016010"
                            onChange={onChange}
                          />
                        ) : (
                          HUDContractNumber
                        )}
                      </Col>
                    </Row>
                  </>
                )}
              </Col>
            </Row>
            <Row>
              <Col xs={12} sm={6}>
                <Row>
                  <Col xs={12} md={5}>
                    <strong>
                      <FormattedMessage {...messages.altXMLPropertyName} />
                    </strong>
                  </Col>
                  <Col xs={12} md={7}>
                    {editMode ? (
                      <TextInput
                        name="setup.XMLPropertyName"
                        value={XMLPropertyName}
                        editMode={editMode}
                        onChange={({ target: { name, value } }) => {
                          onChange({
                            target: {
                              name,
                              value: stripOnlyWhiteSpaceToNull(value),
                            },
                          });
                        }}
                        maxLength={'100'}
                      />
                    ) : (
                      displayAsDashesIfEmpty(XMLPropertyName)
                    )}
                  </Col>
                </Row>
              </Col>
              <Col xs={12} sm={6}>
                <Row>
                  <Col xs={12} md={5}>
                    <strong>
                      <FormattedMessage {...messages.overrideQualification} />
                    </strong>
                  </Col>
                  <Col
                    xs={12}
                    md={7}
                    className={
                      isOverrideQualificationEnabled
                        ? 'integration-on'
                        : 'integration-off'
                    }
                  >
                    {editMode ? (
                      <Switch
                        name="setup.isOverrideQualificationEnabled"
                        id="override-qualification-switch"
                        checked={isOverrideQualificationEnabled}
                        disabled={!editMode}
                        onChange={handleSwitchChange}
                      />
                    ) : isOverrideQualificationEnabled ? (
                      <FormattedMessage {...integrationSetupMessages.on} />
                    ) : (
                      <FormattedMessage {...integrationSetupMessages.off} />
                    )}
                  </Col>
                </Row>
              </Col>
            </Row>
            <Row>
              <Col xs={12} sm={6}>
                <Row>
                  <Col xs={12} md={5}>
                    <strong>
                      <FormattedMessage {...messages.altXMLProjectName} />
                      <sup>*</sup>
                      <Tooltip
                        component="span"
                        placement="top"
                        variant="light"
                        title={
                          <FormattedMessage
                            {...messages.altXMLProjectNameTooltip}
                          />
                        }
                      >
                        <AlertInfoIcon
                          sx={{
                            marginRight: 0.5,
                            position: 'relative',
                            top: 2,
                            '& path': { stroke: 'white !important' },
                          }}
                          fontSize={'small'}
                          strokeWidth={0.5}
                        />
                      </Tooltip>
                    </strong>
                  </Col>
                  <Col xs={12} md={7}>
                    {editMode ? (
                      <TextInput
                        name="setup.XMLProjectName"
                        value={XMLProjectName}
                        editMode={editMode}
                        onChange={({ target: { name, value } }) => {
                          onChange({
                            target: {
                              name,
                              value: stripOnlyWhiteSpaceToNull(value),
                            },
                          });
                        }}
                        maxLength={'100'}
                      />
                    ) : (
                      displayAsDashesIfEmpty(XMLProjectName)
                    )}
                  </Col>
                </Row>
              </Col>
            </Row>
          </div>
          {
            /* RD Setup */
            hasRDProgram && (
              <>
                <Divider sx={{ marginTop: '16px', marginBottom: '16px' }} />
                <RDSetup
                  editMode={editMode}
                  onChange={onChange}
                  values={RDSetupValues}
                />
              </>
            )
          }
          {hasHOMEProgram && (
            <>
              <Divider sx={{ marginTop: '16px', marginBottom: '16px' }} />
              <HOMESetup
                onChange={onChange}
                editMode={editMode}
                values={HOMESetupValues}
              />
            </>
          )}
          {hasHTFProgram && (
            <>
              <Divider sx={{ marginY: 2 }} />
              <HTFSetup
                onChange={onChange}
                promptToaster={promptToaster}
                isLoadingPassbookRateData={isLoadingPassbookRates}
                editMode={editMode}
                values={HTFSetupValues}
              />
            </>
          )}
        </>
      )}
    </Card>
  );
}

export default AffordableSetup;
